import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/gameConstants';
import { Button } from './Button';
import {
  getPanelBounds,
  getScrollContentWidth,
  getScrollPanelWidth,
  getTitleY,
  getUnifiedScrollPanelHeight,
  SCROLL_PANEL_REFERENCE_LINES,
} from './PanelLayout';
import { UIStyle } from './UIStyle';

export interface MenuScrollPanelOptions {
  title: string;
  lines: string[];
  isCompact: boolean;
  buttonHeight: number;
  onClose: () => void;
}

export interface MenuScrollPanelHandle {
  container: Phaser.GameObjects.Container;
  backButton: Button;
  destroyExtras: () => void;
}

function measureBodyHeight(
  scene: Phaser.Scene,
  body: string,
  contentWidth: number,
  fontPx: number,
  lineSpacing: number,
): number {
  const text = scene.add.text(0, 0, body, {
    ...UIStyle.panelBody(`${fontPx}px`),
    align: 'left',
    lineSpacing,
    wordWrap: { width: contentWidth },
    fixedWidth: contentWidth,
  }).setOrigin(0, 0);
  const height = text.height;
  text.destroy();
  return height;
}

export function createMenuScrollPanel(
  scene: Phaser.Scene,
  options: MenuScrollPanelOptions,
): MenuScrollPanelHandle {
  const { title, lines, isCompact, buttonHeight, onClose } = options;

  const panelW = getScrollPanelWidth(isCompact);
  const contentWidth = getScrollContentWidth(panelW);
  const body = lines.join('\n');

  const fontPx = isCompact ? 15 : 16;
  const lineSpacing = isCompact ? 9 : 10;

  // Kotak selalu sama ukuran Credit — teks panjang di-scroll di dalamnya
  const refBody = SCROLL_PANEL_REFERENCE_LINES.join('\n');
  const refContentH = measureBodyHeight(scene, refBody, contentWidth, fontPx, lineSpacing);
  const targetH = getUnifiedScrollPanelHeight(refContentH);
  const bounds = getPanelBounds(scene, panelW, targetH, { scroll: true });
  const {
    panelX,
    panelY,
    panelTop,
    panelH,
    panelW: clampedW,
    contentTop,
    contentMaxH,
    backButtonY,
  } = bounds;

  const container = scene.add.container(0, 0).setDepth(500);

  const overlay = UIStyle.createDarkOverlay(scene, GAME_WIDTH, GAME_HEIGHT, 0.84);

  const panel = UIStyle.createModalSurface(scene, panelX, panelY, clampedW, panelH);

  const contentCardPadX = isCompact ? 18 : 22;
  const contentCardPadY = isCompact ? 14 : 16;
  const contentCard = UIStyle.createContentCard(
    scene,
    panelX - contentWidth / 2 - contentCardPadX,
    contentTop - contentCardPadY,
    contentWidth + contentCardPadX * 2,
    contentMaxH + contentCardPadY * 2,
  );

  const titleText = scene.add.text(panelX, getTitleY(panelTop, true), title, {
    ...UIStyle.panelTitle(isCompact ? '26px' : '30px'),
  }).setOrigin(0.5);

  const divider = scene.add.rectangle(
    panelX,
    contentTop - (isCompact ? 22 : 24),
    clampedW - 72,
    3,
    0xd4a373,
    0.45,
  );

  const content = scene.add.text(
    panelX - contentWidth / 2,
    contentTop,
    body,
    {
      ...UIStyle.panelBody(`${fontPx}px`),
      align: 'left',
      lineSpacing,
      wordWrap: { width: contentWidth },
      fixedWidth: contentWidth,
    },
  ).setOrigin(0, 0);

  const maskShape = scene.make.graphics({ x: 0, y: 0 });
  maskShape.fillStyle(0xffffff);
  maskShape.fillRect(panelX - contentWidth / 2, contentTop, contentWidth, contentMaxH);
  maskShape.setVisible(false);
  content.setMask(maskShape.createGeometryMask());

  const extras: Phaser.GameObjects.GameObject[] = [];
  const cleanupFns: (() => void)[] = [() => maskShape.destroy()];

  const maxScroll = Math.max(0, content.height - contentMaxH);
  if (maxScroll > 0) {
    let scrollOffset = 0;
    const baseY = contentTop;
    let dragStartY = 0;
    let dragStartOffset = 0;

    const scrollZone = scene.add.rectangle(
      panelX,
      contentTop + contentMaxH / 2,
      contentWidth,
      contentMaxH,
      0xffffff,
      0,
    );
    scrollZone.setInteractive({ draggable: false });
    extras.push(scrollZone);

    scrollZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStartY = pointer.y;
      dragStartOffset = scrollOffset;
    });
    scrollZone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      scrollOffset = Phaser.Math.Clamp(
        dragStartOffset + (pointer.y - dragStartY),
        -maxScroll,
        0,
      );
      content.y = baseY + scrollOffset;
    });

    const wheelHandler = (
      _pointer: Phaser.Input.Pointer,
      _gameObjects: Phaser.GameObjects.GameObject[],
      _deltaX: number,
      deltaY: number,
    ) => {
      scrollOffset = Phaser.Math.Clamp(scrollOffset - deltaY * 0.35, -maxScroll, 0);
      content.y = baseY + scrollOffset;
    };
    scene.input.on('wheel', wheelHandler);
    cleanupFns.push(() => scene.input.off('wheel', wheelHandler));
  }

  const btnW = Math.min(clampedW - 80, isCompact ? 300 : 340);
  const backButton = new Button(scene, {
    x: panelX,
    y: backButtonY,
    text: 'Kembali',
    width: btnW,
    height: buttonHeight,
    fontSize: isCompact ? '17px' : '18px',
    depth: 600,
    onClick: onClose,
  });

  container.add([overlay, panel, contentCard, titleText, divider, content, ...extras]);
  overlay.on('pointerup', onClose);
  panel.on('pointerup', (p: Phaser.Input.Pointer) => p.event?.stopPropagation());

  return {
    container,
    backButton,
    destroyExtras: () => {
      cleanupFns.forEach((fn) => fn());
    },
  };
}
